/**
 * Money and quantities: one formatter for the whole UI (#91).
 *
 * Before it every screen formatted on its own: the deal table printed `1 250 000`, the
 * board `1250000 RUB`, a report `1 250 000,00 ₽`. Switching "Table / Board" changed how the
 * same figure looked, and an accountant compared formats instead of sums. One rule:
 *
 * - **money**: digit grouping (a no-break space in ru), exactly two fraction digits, the
 *   currency sign (`₽`, `€`, `$`). The currency is never implied: without a code the sum
 *   is printed without a sign rather than as roubles, and a code with no symbol is printed
 *   as the code (`5,00 XYZ`);
 * - **quantity** is not money: no padding zeros (`120`, not `120,00`), no currency;
 * - **no value**: a dash `—` for both (not zero: zero is a statement).
 *
 * The value is accepted as a string and formatted **as a string**: `numeric(19,4)` does not
 * fit a double, and `Number("12345678901234567.89")` prints kopecks that exist neither in
 * the database nor on the invoice. A number is accepted too: `JSON.parse` already made it
 * one, and nothing can make it more precise than it is.
 *
 * Direct `toLocaleString`/`Intl.NumberFormat` for money outside this module is refused by
 * `format/noStrayMoneyFormatting.test.ts`: otherwise the drift returns with the next screen.
 */

/** A no-break space: a unit never wraps away from its number. */
const NBSP = String.fromCharCode(0xa0)

/** Printed in place of a missing sum or quantity. */
export const NO_VALUE = '—'

/** A sum as the server sends it: a `numeric` string, a number, or nothing. */
export type Amount = string | number | null | undefined

/** A list row's cell as an amount: anything but a string or a number is no amount. */
export function asAmount(value: unknown): Amount {
  return typeof value === 'string' || typeof value === 'number' ? value : null
}

/** A list row's cell as a currency code, or null. */
export function asCurrency(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

/** Fraction digits of money. The schema keeps four, the screen shows kopecks. */
const MONEY_DIGITS = 2

/** Fraction digits a quantity may show: what the schema keeps (`numeric(19,4)`), no more. */
const QUANTITY_DIGITS = 4

interface Decimal {
  negative: boolean
  /** Integer digits, no leading zeros (except zero itself). */
  integer: string
  /** Fraction digits, exactly as many as asked for. */
  fraction: string
}

const DECIMAL = /^([+-])?(\d*)(?:[.,](\d*))?$/

/** A plain decimal string for any finite number, never exponent notation. */
const PLAIN = new Intl.NumberFormat('en-US', { useGrouping: false, maximumFractionDigits: 20 })

/**
 * Parses and rounds half away from zero (as BigDecimal HALF_UP on the server) to
 * {@code digits}, by string arithmetic: no step goes through a double.
 */
function toDecimal(value: string | number, digits: number): Decimal | null {
  let text: string
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null
    text = PLAIN.format(value)
  } else {
    text = value.trim()
  }
  const match = DECIMAL.exec(text)
  if (!match) return null
  const integerDigits = match[2] ?? ''
  const fractionDigits = match[3] ?? ''
  if (integerDigits === '' && fractionDigits === '') return null
  const negative = match[1] === '-'
  const kept = fractionDigits.slice(0, digits).padEnd(digits, '0')
  const next = fractionDigits.charAt(digits)
  let scaled = BigInt((integerDigits || '0') + kept)
  if (next !== '' && next >= '5') scaled += 1n
  const unit = 10n ** BigInt(digits)
  const integer = (scaled / unit).toString()
  const fraction = digits === 0 ? '' : (scaled % unit).toString().padStart(digits, '0')
  // "-0.001" rounds to zero, and a minus in front of zero is noise, not a sign.
  return { negative: negative && scaled !== 0n, integer, fraction }
}

const FORMATS = new Map<string, Intl.NumberFormat>()

function numberFormat(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(options)}`
  let format = FORMATS.get(key)
  if (!format) {
    format = new Intl.NumberFormat(locale, options)
    FORMATS.set(key, format)
  }
  return format
}

/**
 * Renders a decimal through a locale pattern without ever handing the value to Intl as a
 * double: the pattern (sign, symbol, spacing, decimal mark) comes from formatting ±1, the
 * grouped integer from formatting a BigInt — exact at any size, in every engine.
 */
function render(decimal: Decimal, locale: string, options: Intl.NumberFormatOptions): string {
  const digits = decimal.fraction.length
  const pattern = numberFormat(locale, {
    ...options,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).formatToParts(decimal.negative ? -1 : 1)
  const grouped = numberFormat(locale, { maximumFractionDigits: 0 }).format(BigInt(decimal.integer))
  let integerDone = false
  return pattern
    .map((part) => {
      if (part.type === 'integer') {
        if (integerDone) return ''
        integerDone = true
        return grouped
      }
      if (part.type === 'fraction') return decimal.fraction
      return part.value
    })
    .join('')
}

/** Whether Intl accepts the code as a currency (any well-formed ISO 4217 code). */
function currencyOptions(currency: string): Intl.NumberFormatOptions | null {
  const options: Intl.NumberFormatOptions = {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
  }
  try {
    numberFormat('en', options)
    return options
  } catch {
    return null
  }
}

/**
 * A sum: `1 250 000,00 ₽`, `-1 250,50 €`, `5,00 XYZ`; without a currency `1 250 000,00`.
 *
 * A string that does not parse as a number is printed as is: hiding it would pass a
 * filled field off as an empty one.
 */
export function formatMoney(
  value: Amount,
  currency: string | null | undefined,
  locale: string,
  options: { fractionDigits?: number } = {},
): string {
  if (value == null || value === '') return NO_VALUE
  const decimal = toDecimal(value, options.fractionDigits ?? MONEY_DIGITS)
  if (!decimal) return String(value)
  const code = currency?.trim().toUpperCase() ?? ''
  if (code === '') return render(decimal, locale, {})
  const style = currencyOptions(code)
  // A code Intl rejects is still shown — as a code, never silently as roubles.
  return style ? render(decimal, locale, style) : `${render(decimal, locale, {})} ${code}`
}

/**
 * Totals in several currencies, joined by a plus rather than added up.
 *
 * Adding roubles to euros means inventing a one-to-one rate. An empty list is zero without
 * a currency: an empty column has none, and calling it roubles says more than is known.
 */
export function formatMoneyTotals(
  totals: readonly { currency: string; amount: string | number }[],
  locale: string,
): string {
  if (totals.length === 0) return formatMoney(0, null, locale)
  return totals.map((total) => formatMoney(total.amount, total.currency, locale)).join(' + ')
}

/**
 * A quantity: `120`, `1 250,5`, `0,125 kg`: no padding zeros and no currency.
 *
 * 120 pieces never become "120,00 ₽": a quantity has neither kopecks nor a sign.
 */
export function formatQuantity(
  value: Amount,
  locale: string,
  unit?: string | null,
  options: { fractionDigits?: number } = {},
): string {
  if (value == null || value === '') return NO_VALUE
  // A unit's own precision (`0,125 kg` at three digits) is shown as declared, zeros kept.
  const fixed = options.fractionDigits
  const decimal = toDecimal(value, fixed ?? QUANTITY_DIGITS)
  if (!decimal) return String(value)
  const trimmed = fixed === undefined ? { ...decimal, fraction: decimal.fraction.replace(/0+$/, '') } : decimal
  const text = render(trimmed, locale, {})
  return unit ? `${text}${NBSP}${unit}` : text
}
